import { isReadOnlyAdmin } from "@/lib/auth";
import { createFileRoute, getRouteApi } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod/v4";
import { useQuery } from "@tanstack/react-query";
import { useMemo, type ReactNode } from "react";
import { BillingUpgradeBanner } from "@/components/billing-upgrade-banner";
import { getMessageFromError, ProviderIssueError } from "@/lib/api-errors";
import {
  applicationApi,
  ApplicationConfigNotFoundError,
} from "@/lib/application-api";
import { fxQueryKeys } from "@/lib/fx-query-keys";
import { payinAccountsWithAggregatedBalancesQuery } from "@/lib/queries";
import { queryRetry } from "@/lib/query-retry";
import {
  STABLECOIN_CURRENCIES,
  useStablecoinRatesEnabled,
} from "@/lib/stablecoin-rates";
import { walletBalancesQuery } from "../-components/wallet-balances/use-wallet-balances";
import { useIsStablecoinsLive } from "@/modules/stablecoins/stablecoins-live";
import { ExchangeWalletSection } from "../-components/exchange-wallet-section/exchange-wallet-section";
import { useIsFXEnabled } from "../-components/is-exchange-enabled";
import { RateChartCard } from "./-components/landing/rate-history/rate-chart";
import {
  buildDefaultPayInAccountIdByCurrency,
  buildDefaultVaNameByCurrency,
  buildSourceWalletOptions,
  buildVaBalanceByCurrency,
  FX_DISABLED_DESTINATION_CURRENCIES,
  type DestinationAvailability,
} from "./-components/landing/trade-panel-state";
import { TradePanel } from "./-components/landing/trade-panel";
import {
  useTradeSelection,
  type TradeSelectionInput,
} from "./-components/landing/use-trade-selection";
import {
  resolveBulkRatesAndRatesPanelAxes,
  useExchangeRatesFeed,
} from "./-components/landing/use-exchange-rates-feed";

const searchParamsSchema = z.object({
  applicationId: z.string().optional(),
  /** Opens the trade panel on this currency, e.g. USDT from the stablecoin banner. */
  destination: z.string().optional(),
});

export const Route = createFileRoute("/(app)/_main-layout/_exchange/exchange/")(
  {
    component: ExchangePageComponent,
    validateSearch: zodValidator(searchParamsSchema),
    loader: ({ context }) => {
      // Pay-in balances are independent of bulk rates and the rest of the page;
      // prefetch without blocking route transition.
      void context.queryClient.prefetchQuery(
        payinAccountsWithAggregatedBalancesQuery(
          context.merchantId,
          context.accessToken,
          context.selectedApplicationId,
        ),
      );
    },
  },
);

const exchangePageRoute = getRouteApi(
  "/(app)/_main-layout/_exchange/exchange/",
);

const EMPTY_DESTINATION_AVAILABILITY: readonly DestinationAvailability[] = [];

function ExchangePageComponent() {
  const isFxEnabled = useIsFXEnabled();
  return <ExchangePageContent isFxEnabled={isFxEnabled} />;
}

/** What the exchange page loads: eligibility, balances, live rates and the trade panel's input. */
function useExchangePageData(isFxEnabled: boolean | undefined) {
  const context = exchangePageRoute.useRouteContext();
  const { destination: requestedDestination } = exchangePageRoute.useSearch();

  const eligibilityQuery = useQuery({
    queryKey: fxQueryKeys.eligibility(context.selectedApplicationId),
    queryFn: () =>
      applicationApi.getFxEligibility(
        context.selectedApplicationId,
        context.accessToken,
      ),
    enabled: isFxEnabled === true,
    retry: queryRetry(
      (_, error) =>
        !(
          error instanceof ApplicationConfigNotFoundError ||
          error instanceof ProviderIssueError
        ),
    ),
  });
  const payinBalancesQuery = useQuery(
    payinAccountsWithAggregatedBalancesQuery(
      context.merchantId,
      context.accessToken,
      context.selectedApplicationId,
    ),
  );

  const payinBalanceEntries = payinBalancesQuery.data ?? [];
  const vaBalanceByCurrency = buildVaBalanceByCurrency(payinBalanceEntries);

  const stablecoinRatesEnabled = useStablecoinRatesEnabled();
  // Once trading is live, USDC/USDT are only offered through the merchant's
  // assigned wholesale corridors, not as greyed-out "coming soon" options.
  const stablecoinsLive = useIsStablecoinsLive();
  const ratesAxes = useMemo(
    () =>
      resolveBulkRatesAndRatesPanelAxes(
        isFxEnabled,
        eligibilityQuery.data,
        stablecoinRatesEnabled,
      ),
    [isFxEnabled, eligibilityQuery.data, stablecoinRatesEnabled],
  );
  const ratesFeed = useExchangeRatesFeed(ratesAxes.bulkRatePairs);

  const walletQuery = useQuery(
    walletBalancesQuery({
      isFxEnabled: isFxEnabled === true,
      applicationId: context.selectedApplicationId,
    }),
  );
  const walletBalanceByCurrency = useMemo(
    () =>
      new Map(
        (walletQuery.data ?? []).map((wallet) => [
          wallet.currency,
          wallet.availableBalance,
        ]),
      ),
    [walletQuery.data],
  );
  const isReadOnly = isReadOnlyAdmin(context.authorities);
  const tradeSelectionInput: TradeSelectionInput = {
    // A read-only admin sees no trade panel, so nothing is checked for one.
    enabled: !isReadOnly,
    isFxEnabled,
    isPayinBalancesLoading: payinBalancesQuery.isLoading,
    isEligibilityLoading: eligibilityQuery.isLoading,
    isEligibilityError: eligibilityQuery.isError,
    sourceWalletOptions: buildSourceWalletOptions({
      isFxEnabled,
      sourceWalletOptions: eligibilityQuery.data?.sourceWalletOptions,
      gbpVaBalance: vaBalanceByCurrency.get("GBP") ?? 0,
    }),
    destinationCurrencies: isFxEnabled
      ? (eligibilityQuery.data?.destinationCurrencies ?? [])
      : [...FX_DISABLED_DESTINATION_CURRENCIES],
    wholesaleCorridors: eligibilityQuery.data?.wholesaleCorridors,
    supportedCurrencyPairs: isFxEnabled
      ? eligibilityQuery.data?.supportedCurrencyPairs
      : undefined,
    destinationAvailability:
      eligibilityQuery.data?.destinationAvailability ??
      EMPTY_DESTINATION_AVAILABILITY,
    destinationBeneficiaries:
      eligibilityQuery.data?.destinationBeneficiaries ?? [],
    defaultVaNameByCurrency: buildDefaultVaNameByCurrency(payinBalanceEntries),
    defaultPayInAccountIdByCurrency:
      buildDefaultPayInAccountIdByCurrency(payinBalanceEntries),
    vaBalanceByCurrency,
    exchangeRates: ratesFeed.exchangeRates,
    comingSoonDestinationCurrencies:
      stablecoinRatesEnabled && !stablecoinsLive
        ? [...STABLECOIN_CURRENCIES]
        : undefined,
    walletBalanceByCurrency,
    initialDestinationCurrency: requestedDestination,
  };
  return {
    isReadOnly,
    eligibilityErrorMessage: getMessageFromError(eligibilityQuery.error),
    ratesAxes,
    ratesFeed,
    tradeSelectionInput,
  };
}

function ExchangePageContent({
  isFxEnabled,
}: {
  readonly isFxEnabled: boolean | undefined;
}) {
  const { isReadOnly, eligibilityErrorMessage, ratesAxes, ratesFeed, ...data } =
    useExchangePageData(isFxEnabled);
  const { exchangeRates } = ratesFeed;
  // The page owns what the trade panel sets up, so the chart and wallets follow the trade.
  const tradeSelection = useTradeSelection(data.tradeSelectionInput);
  const tradePreview = isReadOnly ? undefined : tradeSelection.preview;

  // The chart follows the pair in the trade panel, FX on or off.
  const chartSource = tradeSelection.source.currency || "GBP";
  const chartDestination =
    tradeSelection.destination.shown ||
    ratesAxes.ratesPanelDestinationCurrencies.find(
      (currency) => currency !== chartSource,
    ) ||
    "";

  return (
    <ExchangeLayout
      isFxEnabled={isFxEnabled}
      tradePanel={
        isReadOnly ? null : (
          <TradePanel
            selection={tradeSelection}
            eligibilityErrorMessage={eligibilityErrorMessage}
          />
        )
      }
      rateChart={
        <RateChartCard
          sourceCurrency={chartSource}
          destinationCurrency={chartDestination}
          liveRate={exchangeRates[`${chartSource}-${chartDestination}`]}
        />
      }
      walletSection={<ExchangeWalletSection tradePreview={tradePreview} />}
    />
  );
}

/**
 * The trade panel beside the rate chart. FX on: the wallets follow below. FX off: the same panel
 * shows indicative rates, with a notice in place of Make trade. Still loading: the panel alone.
 */
function ExchangeLayout({
  isFxEnabled,
  tradePanel,
  rateChart,
  walletSection,
}: {
  isFxEnabled: boolean | undefined;
  tradePanel: ReactNode;
  rateChart: ReactNode;
  walletSection: ReactNode;
}) {
  if (isFxEnabled === undefined) return tradePanel;
  return (
    <>
      <BillingUpgradeBanner />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        {tradePanel ? <div className="lg:col-span-5">{tradePanel}</div> : null}
        <div className={tradePanel ? "lg:col-span-7" : "lg:col-span-12"}>
          {rateChart}
        </div>
      </div>
      {isFxEnabled ? walletSection : null}
    </>
  );
}
